/**
 * FeatureGrid — Phase E split (May 6, 2026).
 *
 * "Everything You Need" 3-card section of LandingPage. Pure
 * presentational. Markup, classes, image fallbacks, and SVG inline
 * fallback paths are byte-identical.
 */
import { Calendar } from 'lucide-react';

const FeatureCard = ({
  imgSrc,
  title,
  description,
  hoverBorderClass,
  fallbackBgClass,
  fallbackContent,
}) => (
  <div className={`rounded-xl bg-slate-800/30 border border-slate-700/50 ${hoverBorderClass} transition-colors overflow-hidden group`}>
    <div className="aspect-video bg-slate-900 overflow-hidden">
      <img
        src={imgSrc}
        alt={title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        onError={(e) => {
          e.target.style.display = 'none';
          e.target.nextSibling.style.display = 'flex';
        }}
      />
      <div className={`w-full h-full ${fallbackBgClass} items-center justify-center hidden`}>
        {fallbackContent}
      </div>
    </div>
    <div className="p-6">
      <h4 className="font-heading font-semibold text-white text-lg mb-2">{title}</h4>
      <p className="text-slate-400 text-sm leading-relaxed">{description}</p>
    </div>
  </div>
);

const WallElevationFallback = (
  <svg className="w-16 h-16 text-teal-400/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
  </svg>
);

const MeasurementsFallback = (
  <svg className="w-16 h-16 text-teal-400/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
  </svg>
);

const BookingFallback = <Calendar className="w-16 h-16 text-amber-400/50" />;

const FeatureGrid = () => (
  <section className="py-12 sm:py-16 px-4 sm:px-8">
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-12">
        <h3 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">
          Everything You Need
        </h3>
        <p className="text-slate-400 max-w-xl mx-auto">
          Our platform provides all the tools you need to design and order your perfect custom closet.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
        <FeatureCard
          imgSrc="/feature-wall-elevation.png"
          title="Wall Elevation Views"
          description="Design each wall with precise measurements and component placement. See your closet from every angle."
          hoverBorderClass="hover:border-teal-500/30"
          fallbackBgClass="bg-gradient-to-br from-teal-900/50 to-slate-900"
          fallbackContent={WallElevationFallback}
        />
        <FeatureCard
          imgSrc="/feature-measurements.png"
          title="Accurate Measurements"
          description="Professional-grade dimension tracking in inches, feet, or metric. Every measurement precisely calculated."
          hoverBorderClass="hover:border-teal-500/30"
          fallbackBgClass="bg-gradient-to-br from-teal-900/50 to-slate-900"
          fallbackContent={MeasurementsFallback}
        />
        <FeatureCard
          imgSrc="/feature-booking.png"
          title="Book & Customize"
          description="Book a consultation - with or without a design. We'll help you create the perfect closet for your space."
          hoverBorderClass="hover:border-amber-500/30"
          fallbackBgClass="bg-gradient-to-br from-amber-900/50 to-slate-900"
          fallbackContent={BookingFallback}
        />
      </div>
    </div>
  </section>
);

export default FeatureGrid;
